import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Trash2, Plus, Minus, Edit2 } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { FairViewsBadge } from '../components/FairViewsWidgets';
import { useCart } from '../context/CartContext';
import { formatPrice } from '../utils/priceFormatter';

const CartPage = () => {
  const navigate = useNavigate();
  const { cartItems, updateQuantity, removeFromCart, getCartTotal, getCartCount } = useCart();

  const calculateSubtotal = () => {
    return getCartTotal();
  };

  const calculateShipping = () => {
    const subtotal = calculateSubtotal();
    return subtotal >= 150 ? 0 : 19.20;
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateShipping();
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header with Navigation */}
      <Header />

      {/* Breadcrumb */}
      <div className="bg-gray-50 border-b">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="text-sm text-gray-600">
            <span className="hover:text-gray-900 cursor-pointer" onClick={() => navigate('/')}>Accueil</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Panier</h1>

        <div className="grid grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="col-span-2">
            {cartItems.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-600 mb-6">Il n'y a plus d'articles dans votre panier</p>
                <button
                  onClick={() => navigate('/')}
                  className="inline-flex items-center gap-2 text-gray-700 hover:text-gray-900"
                >
                  <ChevronLeft size={20} />
                  Continuer vos achats
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {cartItems.map((item, index) => {
                  const unitPrice = item.totalPrice / item.configuration.quantity;
                  const config = item.configuration;
                  const isInstallation = item.product.isInstallationService;
                  
                  return (
                    <div key={index} className="border rounded-lg p-6 flex gap-6">
                      {/* Product Thumbnail */}
                      {isInstallation ? (
                        <div className="w-40 h-40 bg-brand/10 rounded border flex items-center justify-center">
                          <svg xmlns="http://www.w3.org/2000/svg" className="w-16 h-16 text-brand" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                        </div>
                      ) : (
                        <img 
                          src={item.product.thumbnail || item.product.image} 
                          alt={item.product.name}
                          className="w-40 h-40 object-cover rounded border"
                        />
                      )}
                      
                      {/* Product Details */}
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-gray-900 mb-3">{item.product.name}</h3>
                        
                        {/* Configuration Details - Installation Service */}
                        {isInstallation ? (
                          <div className="space-y-2 mb-4">
                            <div className="text-sm">
                              <span className="font-semibold text-gray-700">Type de store:</span>{' '}
                              <span className="text-gray-600">{config.storeTypeLabel}</span>
                            </div>
                            <div className="text-sm">
                              <span className="font-semibold text-gray-700">Largeur de la toile:</span>{' '}
                              <span className="text-gray-600">{config.largeur} cm</span>
                            </div>
                            {config.isEtage && (
                              <div className="text-sm">
                                <span className="font-semibold text-gray-700">Installation en étage:</span>{' '}
                                <span className="text-amber-600">Oui (+20%)</span>
                              </div>
                            )}
                            {config.isHigher3m && (
                              <div className="text-sm">
                                <span className="font-semibold text-gray-700">Hauteur &gt; 3m:</span>{' '}
                                <span className="text-amber-600">Oui (+20%)</span>
                              </div>
                            )}
                          </div>
                        ) : (
                        /* Configuration Details - Regular Product */
                        <div className="space-y-2 mb-4">
                          {/* Dimensions */}
                          <div className="text-sm">
                            <span className="font-semibold text-gray-700">Dimensions:</span>{' '}
                            <span className="text-gray-600">
                              {config.largeur} × {config.projection} cm ({((config.largeur * config.projection) / 10000).toFixed(2)} m²)
                            </span>
                          </div>
                          
                          {/* Fixations */}
                          <div className="text-sm">
                            <span className="font-semibold text-gray-700">Fixation haute:</span>{' '}
                            <span className="text-gray-600 capitalize">{config.fixationHaute?.replace(/-/g, ' ')}</span>
                          </div>
                          <div className="text-sm">
                            <span className="font-semibold text-gray-700">Fixation basse:</span>{' '}
                            <span className="text-gray-600 capitalize">{config.fixationBasse?.replace(/-/g, ' ')}</span>
                          </div>
                          
                          {/* Lambrequin */}
                          <div className="text-sm">
                            <span className="font-semibold text-gray-700">Lambrequin:</span>{' '}
                            <span className="text-gray-600">
                              {config.lambrequin === 'sans' && 'Sans lambrequin'}
                              {config.lambrequin === 'droit' && `Coupe droite (${config.hauteurLambrequin} cm)`}
                              {config.lambrequin === 'ondule' && `Vague 1 (${config.hauteurLambrequin} cm)`}
                              {config.lambrequin === 'vagues' && `Vague 2 (${config.hauteurLambrequin} cm)`}
                            </span>
                          </div>
                          
                          {/* Options Supplémentaires */}
                          {config.optionsSupp && (
                            <>
                              {config.optionsSupp.renfortLyre && config.renfortLyreDetails && (
                                <div className="text-sm">
                                  <span className="font-semibold text-gray-700">Renfort de lyre:</span>{' '}
                                  <span className="text-gray-600">
                                    {config.renfortLyreDetails.nombre} renfort(s) aux positions:{' '}
                                    {config.renfortLyreDetails.positions?.map(pos => `${Math.round(pos)} cm`).join(', ')}
                                  </span>
                                </div>
                              )}
                              
                              {config.optionsSupp.textePersonnalise && (
                                <div className="text-sm">
                                  <span className="font-semibold text-gray-700">Option:</span>{' '}
                                  <span className="text-gray-600">Jonc magnétique</span>
                                </div>
                              )}
                              
                              {config.optionsSupp.agrafes && (
                                <div className="text-sm">
                                  <span className="font-semibold text-gray-700">Option:</span>{' '}
                                  <span className="text-gray-600">Jonc à montage rapide</span>
                                </div>
                              )}
                              
                              {config.optionsSupp.clips && config.typeAssemblage && (
                                <div className="text-sm">
                                  <span className="font-semibold text-gray-700">Type d'assemblage:</span>{' '}
                                  <span className="text-gray-600">
                                    {config.typeAssemblage === 'haute-tenacite' && 'Toile cousue fil haute ténacité'}
                                    {config.typeAssemblage === 'tenara' && 'Toile cousue fil Tenara™'}
                                    {config.typeAssemblage === 'soudee' && 'Toile soudée'}
                                  </span>
                                </div>
                              )}
                              
                              {config.optionsSupp.oeillets && config.lestageDetails && (
                                <div className="text-sm">
                                  <span className="font-semibold text-gray-700">Lestage du lambrequin:</span>{' '}
                                  <span className="text-gray-600">
                                    Tube Ø{config.lestageDetails.diametreTube}mm, {config.lestageDetails.nombreLests} lest(s)
                                  </span>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                        )}
                        
                        {/* Récapitulatif de confection */}
                        {!isInstallation && config.fabricCalculation && (
                          <div className="mt-3 p-3 bg-blue-50 rounded text-xs border border-blue-200">
                            <p className="font-semibold text-blue-900 mb-1">Récapitulatif de confection:</p>
                            <p className="font-mono text-blue-800 text-sm">{config.fabricCalculation.display}</p>
                            <p className="text-gray-500 text-xs mt-1">
                              Largeur totale | Avancée totale | Nombre de laizes | Métrage total | Poids total
                            </p>
                          </div>
                        )}
                        
                        {/* Pricing and Actions */}
                        <div className="mt-4 pt-4 border-t">
                          <div className="flex items-center justify-between mb-3">
                            {/* Quantity Controls */}
                            <div className="flex items-center gap-3">
                              <span className="text-sm text-gray-600">Quantité:</span>
                              <button
                                onClick={() => updateQuantity(index, config.quantity - 1)}
                                className="p-1 border border-gray-300 rounded hover:bg-gray-100 transition-colors"
                              >
                                <Minus size={16} />
                              </button>
                              <span className="w-12 text-center font-medium">{config.quantity}</span>
                              <button
                                onClick={() => updateQuantity(index, config.quantity + 1)}
                                className="p-1 border border-gray-300 rounded hover:bg-gray-100 transition-colors"
                              >
                                <Plus size={16} />
                              </button>
                            </div>
                            
                            {/* Prices */}
                            <div className="text-right">
                              <div className="text-sm text-gray-600">
                                Prix unitaire: <span className="font-medium">{formatPrice(unitPrice)} €</span>
                              </div>
                              <div className="text-lg font-bold text-gray-900">
                                Total: {formatPrice(item.totalPrice)} €
                              </div>
                            </div>
                          </div>
                          
                          {/* Action Buttons */}
                          <div className="flex items-center gap-3">
                            <button
                              onClick={() => navigate(`/produit/${item.product.id}`)}
                              className="flex items-center gap-2 px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50 transition-colors"
                            >
                              <Edit2 size={16} />
                              Modifier la configuration
                            </button>
                            <button
                              onClick={() => removeFromCart(index)}
                              className="flex items-center gap-2 px-4 py-2 text-sm text-red-600 border border-red-300 rounded hover:bg-red-50 transition-colors"
                            >
                              <Trash2 size={16} />
                              Supprimer
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Order Summary */}
          <div>
            <div className="bg-white border rounded-lg p-6 sticky top-4">
              <button className="w-full py-3 bg-celadon text-white rounded font-medium hover:bg-celadon-light transition-colors mb-4">
                Transformer ce panier en devis
              </button>
              
              <button className="w-full py-3 border border-gray-300 text-gray-700 rounded font-medium hover:bg-gray-50 transition-colors mb-6">
                Sauvegarder ou partager mon panier
              </button>

              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">{getCartCount()} article(s)</span>
                  <span className="font-medium">{getCartCount()}</span>
                </div>
                
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Sous-total</span>
                  <span className="font-medium">{formatPrice(calculateSubtotal())} €</span>
                </div>
                
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Livraison</span>
                  <span className="font-medium">
                    {calculateShipping() === 0 ? 'Gratuite' : `${formatPrice(calculateShipping())} €`}
                  </span>
                </div>
                
                <div className="pt-3 border-t">
                  <div className="flex justify-between text-lg">
                    <span className="font-bold">Total général TTC</span>
                    <span className="font-bold">{formatPrice(calculateTotal())} €</span>
                  </div>
                </div>
              </div>

              <button
                onClick={() => navigate('/commande')}
                disabled={cartItems.length === 0}
                className={`w-full py-4 rounded font-bold text-lg transition-colors ${
                  cartItems.length === 0
                    ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                    : 'bg-celadon text-white hover:bg-celadon-light'
                }`}
              >
                FINALISER MA COMMANDE
              </button>

              {/* Payment Methods */}
              <div className="mt-6 pt-6 border-t">
                <img 
                  src="https://www.ssl-images-osyla.com/img/cms/backgroundNew%20Folder/cartebleureassurance.png"
                  alt="Moyens de paiement"
                  className="w-full mb-3"
                />
                <img 
                  src="https://www.ssl-images-osyla.com/img/cms/cms/Partenaire-cofidis-3x-4x-sans-frais-tadoupika-robe-france.png"
                  alt="Paiement 3x 4x"
                  className="w-full"
                />
              </div>

              {/* FairViews Badge - Avis clients */}
              <div className="mt-6 flex justify-center">
                <FairViewsBadge />
              </div>
            </div>

            {/* Trust Badge */}
            <div className="mt-6 text-center">
              <div className="w-16 h-16 bg-gray-200 rounded-full mx-auto mb-2 flex items-center justify-center">
                <span className="text-2xl">✓</span>
              </div>
              <p className="font-bold text-sm">Protection acheteur</p>
              <div className="flex items-center justify-center gap-1 mt-2">
                <span className="text-yellow-400">★★★★★</span>
              </div>
              <p className="text-lg font-bold">4.82</p>
              <p className="text-xs text-gray-600">Très bien</p>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mt-16">
          <h2 className="text-2xl font-bold mb-6">Questions fréquentes</h2>
          <div className="space-y-4">
            <details className="border rounded-lg p-4">
              <summary className="font-medium cursor-pointer">Le paiement est-il sécurisé ?</summary>
              <p className="mt-3 text-gray-600 text-sm">
                Absolument, nous utilisons le mode de sécurité SSL et 3D SECURE pour nos paiements. 
                Cela signifie que nous garantissons sur notre site aucun enregistrement de vos informations de paiement.
              </p>
            </details>
            
            <details className="border rounded-lg p-4">
              <summary className="font-medium cursor-pointer">Puis-je payer en plusieurs fois ?</summary>
              <p className="mt-3 text-gray-600 text-sm">
                En effet, nous vous proposons le paiement en 3 fois sans frais par carte bancaire. 
                Vous devez utiliser cependant une carte bleue en tant que particulier.
              </p>
            </details>
            
            <details className="border rounded-lg p-4">
              <summary className="font-medium cursor-pointer">Puis-je annuler ma commande ?</summary>
              <p className="mt-3 text-gray-600 text-sm">
                Après avoir passé votre commande, si le statut de celle-ci n'est pas encore passé à l'étape de 
                traitement en cours vous pouvez encore annuler votre commande.
              </p>
            </details>
            
            <details className="border rounded-lg p-4">
              <summary className="font-medium cursor-pointer">Mes données sont-elles sécurisées ?</summary>
              <p className="mt-3 text-gray-600 text-sm">
                Tout à fait, nous tenons particulièrement à rendre vos données anonymes sans les stocker. 
                Nous ne diffuserons aucune information personnelle à des tiers.
              </p>
            </details>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CartPage;
